import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  buildCalendarMap,
  formatInr,
  formatScheduleDate,
} from "./recurringInvoiceUtils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatYmd(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function RecurringCalendarView({
  templates = [],
  selectedDate,
  onSelectDate,
  onOpenTemplate,
  includeProjected = false,
  onToggleProjected,
}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);

  const calendarMap = useMemo(
    () => buildCalendarMap(templates, viewYear, viewMonth, includeProjected),
    [templates, viewYear, viewMonth, includeProjected]
  );

  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth - 1, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(formatYmd(viewYear, viewMonth, day));
  }

  const shiftMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  const selectedEntries = selectedDate ? calendarMap[selectedDate] || [] : [];
  const todayYmd = formatYmd(
    today.getFullYear(),
    today.getMonth() + 1,
    today.getDate()
  );

  return (
    <div className="recurring-calendar">
      <div className="recurring-calendar__toolbar">
        <div className="recurring-calendar__nav">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            <ChevronLeft size={18} />
          </button>
          <h3>
            {MONTH_NAMES[viewMonth - 1]} {viewYear}
          </h3>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">
            <ChevronRight size={18} />
          </button>
        </div>
        <label className="recurring-calendar__projected">
          <input
            type="checkbox"
            checked={includeProjected}
            onChange={(e) => onToggleProjected?.(e.target.checked)}
          />
          Show projected runs
        </label>
      </div>

      <div className="recurring-calendar__weekdays">
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="recurring-calendar__grid">
        {cells.map((dateYmd, idx) => {
          if (!dateYmd) {
            return <div key={`empty-${idx}`} className="recurring-calendar__cell recurring-calendar__cell--empty" />;
          }
          const entries = calendarMap[dateYmd] || [];
          const isSelected = selectedDate === dateYmd;
          const isToday = dateYmd === todayYmd;
          const dayNum = parseInt(dateYmd.split("-")[2], 10);

          return (
            <button
              key={dateYmd}
              type="button"
              className={`recurring-calendar__cell ${isSelected ? "recurring-calendar__cell--selected" : ""} ${isToday ? "recurring-calendar__cell--today" : ""}`}
              onClick={() => onSelectDate?.(isSelected ? null : dateYmd)}
            >
              <span className="recurring-calendar__day">{dayNum}</span>
              {entries.length > 0 && (
                <span className="recurring-calendar__dots" title={`${entries.length} schedule(s)`}>
                  {entries.length <= 3
                    ? entries.map((e, i) => (
                        <i
                          key={`${e.template.id}-${i}`}
                          className={e.projected ? "is-projected" : "is-confirmed"}
                        />
                      ))
                    : (
                      <>
                        <i className="is-confirmed" />
                        <i className="is-projected" />
                        <span className="recurring-calendar__more">+{entries.length - 2}</span>
                      </>
                    )}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div className="recurring-calendar__day-panel">
          <div className="recurring-calendar__day-panel-head">
            <h4>Schedules on {formatScheduleDate(selectedDate)}</h4>
            <button type="button" onClick={() => onSelectDate?.(null)}>
              Clear
            </button>
          </div>
          {selectedEntries.length === 0 ? (
            <p className="recurring-calendar__day-empty">No billing scheduled this day.</p>
          ) : (
            <ul className="recurring-calendar__day-list">
              {selectedEntries.map(({ template, projected }) => (
                <li key={`${template.id}-${projected ? "p" : "c"}`}>
                  <button
                    type="button"
                    className="recurring-calendar__day-item"
                    onClick={() => onOpenTemplate?.(template)}
                  >
                    <span className="recurring-calendar__day-name">{template.template_name}</span>
                    <span className="recurring-calendar__day-customer">{template.customer_name}</span>
                    <span className="recurring-calendar__day-amount">
                      {formatInr(template.grand_total)}
                      {projected ? " · projected" : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
